import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Rectangle, Polygon, Marker, Popup, useMap, useMapEvents, Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { renderToStaticMarkup } from 'react-dom/server';
import { Save, Trash2, Plus, Tag, Tent, Car, Coffee, Info, TreePine, Zap, ShowerHead, Toilet, Waves, X, Move, PenTool, Check, Copy, Layers, Crosshair, Square } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AMENITY_TYPES = [
  { id: 'toilet', label: 'WC / Toalet', Icon: Toilet, color: '#3B82F6' },
  { id: 'shower', label: 'Tuševi', Icon: ShowerHead, color: '#06B6D4' },
  { id: 'parking', label: 'Parking', Icon: Car, color: '#6366F1' },
  { id: 'beach', label: 'Plaža', Icon: Waves, color: '#F59E0B' },
  { id: 'restaurant', label: 'Restoran / Kafić', Icon: Coffee, color: '#EF4444' },
  { id: 'reception', label: 'Recepcija / Info', Icon: Info, color: '#10B981' },
  { id: 'playground', label: 'Igralište', Icon: TreePine, color: '#84CC16' },
  { id: 'electricity', label: 'Struja', Icon: Zap, color: '#F97316' },
];

const PARCEL_COLORS = ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

// Default parcel size in degrees (~8x10m at zoom 19) — legacy rectangle mode
const DEFAULT_PARCEL_W = 0.00008;
const DEFAULT_PARCEL_H = 0.0001;

// ============================================================================
// Geo helpers
// ============================================================================
function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function formatMeters(m) {
  if (m == null) return '';
  if (m < 10) return `${m.toFixed(1)}m`;
  return `${Math.round(m)}m`;
}

function getPolygonCentroid(coords) {
  if (!coords || coords.length === 0) return [0, 0];
  let lat = 0, lng = 0;
  coords.forEach(([la, ln]) => { lat += la; lng += ln; });
  return [lat / coords.length, lng / coords.length];
}

function getRectangleBounds(parcel) {
  const w = parcel.width || DEFAULT_PARCEL_W;
  const h = parcel.height || DEFAULT_PARCEL_H;
  return [
    [parcel.lat - h / 2, parcel.lng - w / 2],
    [parcel.lat + h / 2, parcel.lng + w / 2],
  ];
}

// Snap a candidate point to a 90° angle from the previous vertex when SHIFT is held.
function snapOrthogonal(prev, next) {
  if (!prev) return next;
  const dLat = next[0] - prev[0];
  const dLng = next[1] - prev[1];
  if (Math.abs(dLat) > Math.abs(dLng)) return [next[0], prev[1]];
  return [prev[0], next[1]];
}

// ============================================================================
// Leaflet icons
// ============================================================================
const lucideToHtml = (IconComp, size = 16) =>
  renderToStaticMarkup(<IconComp color="#fff" size={size} strokeWidth={2.25} />);

const createAmenityIcon = (type) => {
  const amenity = AMENITY_TYPES.find(a => a.id === type) || AMENITY_TYPES[0];
  const inner = lucideToHtml(amenity.Icon, 16);
  return L.divIcon({
    className: 'camp-amenity-icon',
    html: `<div style="background:${amenity.color};color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid #fff;">${inner}</div>`,
    iconSize: [32, 32], iconAnchor: [16, 16],
  });
};

const createParcelLabel = (number, color, selected) => L.divIcon({
  className: 'camp-parcel-num',
  html: `<div style="background:${selected ? '#1D4ED8' : color};color:#fff;padding:1px 5px;border-radius:3px;font-weight:700;font-size:11px;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.4);border:${selected ? '2px solid #fff' : 'none'};min-width:20px;text-align:center;">${number}</div>`,
  iconSize: [28, 18], iconAnchor: [14, 9],
});

const vertexIcon = (active) => L.divIcon({
  className: 'vertex-handle',
  html: `<div style="width:${active ? 18 : 14}px;height:${active ? 18 : 14}px;background:#fff;border:3px solid var(--color-primary, #1D4ED8);border-radius:50%;box-shadow:0 2px 5px rgba(0,0,0,0.4);transition:transform 0.1s;cursor:grab;"></div>`,
  iconSize: [active ? 18 : 14, active ? 18 : 14],
  iconAnchor: [active ? 9 : 7, active ? 9 : 7],
});

const midpointIcon = L.divIcon({
  className: 'vertex-midpoint',
  html: `<div style="width:10px;height:10px;background:rgba(255,255,255,0.85);border:2px dashed var(--color-primary, #1D4ED8);border-radius:50%;cursor:copy;"></div>`,
  iconSize: [10, 10], iconAnchor: [5, 5],
});

// ============================================================================
// Map sub-components
// ============================================================================
function MapClickHandler({ mode, onMapClick }) {
  useMapEvents({ click: (e) => { if (mode) onMapClick(e.latlng, e.originalEvent); } });
  return null;
}

function MapDoubleClickHandler({ onDoubleClick }) {
  useMapEvents({ dblclick: (e) => onDoubleClick(e) });
  return null;
}

// Tracks the mouse pos in draw mode so we can show a "rubber band" preview line
function MouseTracker({ enabled, shiftKey, lastPoint, onMove }) {
  useMapEvents({
    mousemove: (e) => {
      if (!enabled) return;
      let pos = [e.latlng.lat, e.latlng.lng];
      if (shiftKey && lastPoint) pos = snapOrthogonal(lastPoint, pos);
      onMove(pos);
    },
  });
  return null;
}

// Sets a crosshair cursor on the map container while a placement/drawing mode is active.
function CursorController({ active }) {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    if (active) el.classList.add('camp-draw-cursor');
    else el.classList.remove('camp-draw-cursor');
    return () => el.classList.remove('camp-draw-cursor');
  }, [active, map]);
  return null;
}

function DraggableParcelMarker({ parcel, selected, onDragEnd, onClick }) {
  const center = parcel.coords && parcel.coords.length >= 3
    ? getPolygonCentroid(parcel.coords)
    : [parcel.lat, parcel.lng];
  return (
    <Marker
      position={center}
      icon={createParcelLabel(parcel.number, parcel.color || '#22C55E', selected)}
      draggable={!parcel.coords}
      eventHandlers={{
        click: (e) => onClick(parcel, e.originalEvent),
        dragend: (e) => {
          if (parcel.coords) return;
          const pos = e.target.getLatLng();
          onDragEnd(parcel.parcel_id, pos.lat, pos.lng);
        },
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// VertexHandle — mounted ONCE per (parcel, vertex) so Leaflet's drag handler
// is never interrupted by React re-renders during the drag gesture.
// All updates flow through stable handlerRef callbacks that read latest state.
// ---------------------------------------------------------------------------
const VertexHandle = React.memo(function VertexHandle({ initialPosition, parcelId, vertexIdx, handlerRef }) {
  return (
    <Marker
      position={initialPosition}
      draggable={true}
      icon={vertexIcon(true)}
      eventHandlers={{
        drag: (e) => handlerRef.current.onMove(parcelId, vertexIdx, e.target.getLatLng()),
        dragend: (e) => handlerRef.current.onMoveEnd(parcelId, vertexIdx, e.target.getLatLng()),
      }}
    />
  );
}, () => true); // never re-render → Leaflet drag survives until user releases

// ---------------------------------------------------------------------------
// PolygonDragHandle — the parcel-number label for polygon parcels; lets admin
// pick up the whole polygon and translate it. Same mount-once memo trick.
// ---------------------------------------------------------------------------
const PolygonDragHandle = React.memo(function PolygonDragHandle({ initialPosition, parcelId, icon, handlerRef }) {
  return (
    <Marker
      position={initialPosition}
      draggable={true}
      icon={icon}
      eventHandlers={{
        click: (e) => handlerRef.current.onClick(parcelId, e.originalEvent),
        dragstart: (e) => handlerRef.current.onDragStart(parcelId, e.target.getLatLng()),
        drag: (e) => handlerRef.current.onDrag(parcelId, e.target.getLatLng()),
        dragend: (e) => handlerRef.current.onDragEnd(parcelId, e.target.getLatLng()),
      }}
    />
  );
}, () => true);


// ============================================================================
// Main editor
// ============================================================================
export default function CampMapEditor() {
  const [parcels, setParcels] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [propertyConfigs, setPropertyConfigs] = useState([]);
  const [mapSettings, setMapSettings] = useState({ lat: 45.327, lng: 14.442, zoom: 18 });
  const [selectedIds, setSelectedIds] = useState([]); // multi-select parcel_ids
  const [mode, setMode] = useState(null); // null | 'parcel' | 'draw' | <amenity-id>
  const [drawCoords, setDrawCoords] = useState([]);
  const [hoverPos, setHoverPos] = useState(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nextNumber, setNextNumber] = useState(1);
  const [parcelSize, setParcelSize] = useState({ w: 1, h: 1 });
  // Sticky zone: any new parcel gets these defaults until admin changes them
  const [stickyZone, setStickyZone] = useState({ name: '', color: PARCEL_COLORS[0] });
  const token = localStorage.getItem('admin_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const editorRef = useRef(null);

  // Ref to always-current parcels list so memoized drag markers can read latest data
  // without being themselves re-rendered (which would break Leaflet drag mid-gesture).
  const parcelsRef = useRef(parcels);
  useEffect(() => { parcelsRef.current = parcels; }, [parcels]);

  // Tracks where a polygon-drag started so we can apply the delta to every vertex.
  const polygonDragStart = useRef(null);

  // Stable handler bag passed to memoized <VertexHandle> and <PolygonDragHandle>.
  // We mutate handlerRef.current on each render so callbacks always see the latest
  // closures, but the marker components themselves never re-render.
  const handlerRef = useRef({
    onMove: () => {},
    onMoveEnd: () => {},
    onClick: () => {},
    onDragStart: () => {},
    onDrag: () => {},
    onDragEnd: () => {},
  });

  // ---- Data fetching ----
  const loadData = useCallback(async () => {
    try {
      const [mapRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/admin/camp-map`, { headers }),
        axios.get(`${API}/api/admin/settings`, { headers }).catch(() => ({ data: { property_configs: [] } })),
      ]);
      const loaded = mapRes.data.parcels || [];
      setParcels(loaded);
      setMarkers(mapRes.data.markers || []);
      if (mapRes.data.settings?.lat) setMapSettings(mapRes.data.settings);
      setPropertyConfigs(settingsRes.data.property_configs || []);
      if (loaded.length > 0) {
        const maxN = Math.max(...loaded.map(p => parseInt(p.number) || 0));
        setNextNumber(maxN + 1);
      }
    } catch (err) {
      console.error('Load camp map error:', err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Keyboard tracking (Shift for ortho snap, Ctrl+D dup, Delete, Esc) ----
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'Shift') setShiftHeld(true);
      if (e.key === 'Escape') {
        if (mode === 'draw') { setDrawCoords([]); setMode(null); }
        else setSelectedIds([]);
      }
      // Ignore shortcuts when user is typing in a form field
      const inField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName);
      if (inField) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd' && selectedIds.length > 0) {
        e.preventDefault();
        duplicateSelected();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault();
        deleteSelected();
      }
    };
    const up = (e) => { if (e.key === 'Shift') setShiftHeld(false); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };

  }, [selectedIds, mode, parcels, drawCoords]);

  // ---- Build lookup: which room IDs are already used by an existing parcel ----
  const usedRoomIds = useMemo(() => {
    const s = new Set();
    parcels.forEach(p => { if (p.room_id) s.add(String(p.room_id)); });
    return s;
  }, [parcels]);

  const allZoneNames = useMemo(() => {
    const set = new Set();
    parcels.forEach(p => { if (p.zone_name) set.add(p.zone_name); });
    if (stickyZone.name) set.add(stickyZone.name);
    return Array.from(set).sort();
  }, [parcels, stickyZone.name]);

  // ---- Persistence helpers ----
  const persistParcel = async (parcel) => {
    const res = await axios.post(`${API}/api/admin/camp-map/parcels`, parcel, { headers });
    return { ...parcel, parcel_id: res.data.parcel_id };
  };

  // ---- Selection helpers ----
  const toggleSelection = (parcelId, multi) => {
    setSelectedIds(prev => {
      if (multi) return prev.includes(parcelId) ? prev.filter(id => id !== parcelId) : [...prev, parcelId];
      return [parcelId];
    });
  };
  const clearSelection = () => setSelectedIds([]);
  const isSelected = (id) => selectedIds.includes(id);
  const selectedParcels = useMemo(() => parcels.filter(p => selectedIds.includes(p.parcel_id)), [parcels, selectedIds]);
  const primarySelected = selectedParcels[0] || null;

  // ---- Drawing handlers ----
  const handleMapClick = async (latlng, origEvent) => {
    if (mode === 'draw') {
      let pt = [latlng.lat, latlng.lng];
      if (origEvent?.shiftKey && drawCoords.length > 0) {
        pt = snapOrthogonal(drawCoords[drawCoords.length - 1], pt);
      }
      setDrawCoords(prev => [...prev, pt]);
      return;
    }
    if (mode === 'parcel') {
      const newParcel = {
        lat: latlng.lat, lng: latlng.lng,
        width: DEFAULT_PARCEL_W * parcelSize.w,
        height: DEFAULT_PARCEL_H * parcelSize.h,
        number: String(nextNumber),
        label: `Parcela ${nextNumber}`,
        room_id: '',
        color: stickyZone.color,
        zone_name: stickyZone.name || '',
      };
      try {
        setSaving(true);
        const saved = await persistParcel(newParcel);
        setParcels(prev => [...prev, saved]);
        setNextNumber(prev => prev + 1);
      } catch (err) { console.error(err); }
      finally { setSaving(false); }
      return;
    }
    if (mode && mode !== 'parcel' && mode !== 'draw') {
      const amenity = AMENITY_TYPES.find(a => a.id === mode);
      const newMarker = { type: mode, lat: latlng.lat, lng: latlng.lng, label: amenity?.label || mode };
      try {
        const res = await axios.post(`${API}/api/admin/camp-map/markers`, newMarker, { headers });
        newMarker.marker_id = res.data.marker_id;
        setMarkers(prev => [...prev, newMarker]);
      } catch (err) { console.error(err); }
      setMode(null);
    }
  };

  const finishDrawing = useCallback(async () => {
    if (drawCoords.length < 3) {
      alert('Potrebne su barem 3 točke za zatvaranje parcele.');
      return;
    }
    const [lat, lng] = getPolygonCentroid(drawCoords);
    const newParcel = {
      lat, lng, coords: drawCoords,
      number: String(nextNumber),
      label: `Parcela ${nextNumber}`,
      room_id: '',
      color: stickyZone.color,
      zone_name: stickyZone.name || '',
    };
    try {
      setSaving(true);
      const saved = await persistParcel(newParcel);
      setParcels(prev => [...prev, saved]);
      setNextNumber(prev => prev + 1);
      setDrawCoords([]);
      setMode(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  }, [drawCoords, nextNumber, stickyZone, headers]);

  const cancelDrawing = () => { setDrawCoords([]); setMode(null); };
  const undoLastPoint = () => setDrawCoords(prev => prev.slice(0, -1));

  // ---- Vertex editing ----
  const moveVertex = (parcelId, vertexIdx, newLat, newLng) => {
    setParcels(prev => prev.map(p => {
      if (p.parcel_id !== parcelId) return p;
      const nc = p.coords.map((c, i) => i === vertexIdx ? [newLat, newLng] : c);
      const [cLat, cLng] = getPolygonCentroid(nc);
      return { ...p, coords: nc, lat: cLat, lng: cLng };
    }));
  };

  const persistParcelById = async (parcelId) => {
    const parcel = parcelsRef.current.find(p => p.parcel_id === parcelId);
    if (!parcel) return;
    try { await axios.post(`${API}/api/admin/camp-map/parcels`, parcel, { headers }); }
    catch (err) { console.error(err); }
  };

  // ---- Polygon whole-shape drag ----
  // Records the starting centroid; on drag we apply the same lat/lng delta to
  // every vertex so the shape translates rigidly.
  const onPolygonDragStart = (parcelId, latlng) => {
    const parcel = parcelsRef.current.find(p => p.parcel_id === parcelId);
    if (!parcel || !parcel.coords) return;
    polygonDragStart.current = {
      parcelId,
      startLatLng: { lat: latlng.lat, lng: latlng.lng },
      coords: parcel.coords.map(c => [c[0], c[1]]),
    };
  };

  const onPolygonDrag = (parcelId, latlng) => {
    const st = polygonDragStart.current;
    if (!st || st.parcelId !== parcelId) return;
    const dLat = latlng.lat - st.startLatLng.lat;
    const dLng = latlng.lng - st.startLatLng.lng;
    const newCoords = st.coords.map(([la, ln]) => [la + dLat, ln + dLng]);
    setParcels(prev => prev.map(p => p.parcel_id === parcelId
      ? { ...p, coords: newCoords, lat: latlng.lat, lng: latlng.lng }
      : p));
  };

  const onPolygonDragEnd = (parcelId) => {
    polygonDragStart.current = null;
    // Bump layout token so VertexHandle markers remount at the new positions.
    setParcels(prev => prev.map(p => p.parcel_id === parcelId
      ? { ...p, _layoutToken: (p._layoutToken || 0) + 1 }
      : p));
    persistParcelById(parcelId);
  };

  // ---- Hookup stable handlers for memoized markers ----
  handlerRef.current.onMove = (pid, vi, ll) => moveVertex(pid, vi, ll.lat, ll.lng);
  handlerRef.current.onMoveEnd = (pid) => persistParcelById(pid);
  handlerRef.current.onClick = (pid, origEvent) => toggleSelection(pid, origEvent?.shiftKey);
  handlerRef.current.onDragStart = onPolygonDragStart;
  handlerRef.current.onDrag = onPolygonDrag;
  handlerRef.current.onDragEnd = onPolygonDragEnd;

  // Insert a new vertex at the midpoint of an edge (between vertex i and i+1)
  const insertVertex = (parcelId, edgeIdx) => {
    setParcels(prev => prev.map(p => {
      if (p.parcel_id !== parcelId) return p;
      const a = p.coords[edgeIdx];
      const b = p.coords[(edgeIdx + 1) % p.coords.length];
      const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const nc = [...p.coords.slice(0, edgeIdx + 1), mid, ...p.coords.slice(edgeIdx + 1)];
      return { ...p, coords: nc, _layoutToken: (p._layoutToken || 0) + 1 };
    }));
    setTimeout(() => persistParcelById(parcelId), 0);
  };

  const handleParcelDrag = async (parcelId, lat, lng) => {
    const updated = parcels.map(p => p.parcel_id === parcelId ? { ...p, lat, lng } : p);
    setParcels(updated);
    const parcel = updated.find(p => p.parcel_id === parcelId);
    if (parcel) {
      try { await axios.post(`${API}/api/admin/camp-map/parcels`, parcel, { headers }); }
      catch (err) { console.error(err); }
    }
  };

  // ---- CRUD ----
  const saveParcel = async (parcel) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/camp-map/parcels`, parcel, { headers });
      setParcels(prev => prev.map(p => p.parcel_id === parcel.parcel_id ? parcel : p));
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const deleteParcel = async (parcelId) => {
    try {
      await axios.delete(`${API}/api/admin/camp-map/parcels/${parcelId}`, { headers });
      setParcels(prev => prev.filter(p => p.parcel_id !== parcelId));
      setSelectedIds(prev => prev.filter(id => id !== parcelId));
    } catch (err) { console.error(err); }
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Obrisati ${selectedIds.length} parcela?`)) return;
    for (const id of selectedIds) {

      await deleteParcel(id);
    }
    setSelectedIds([]);
  };

  // Duplicate selected parcels with a small spatial offset; placement can be fine-tuned by dragging.
  const duplicateSelected = async () => {
    if (selectedParcels.length === 0) return;
    const offsetLat = DEFAULT_PARCEL_H * 1.5;
    const offsetLng = DEFAULT_PARCEL_W * 1.5;
    let nextNum = nextNumber;
    const newOnes = [];
    for (const p of selectedParcels) {
      const baseNum = nextNum++;
      const copy = {
        ...p,
        parcel_id: undefined,
        number: String(baseNum),
        label: `Parcela ${baseNum}`,
        room_id: '', // room_id is unique per parcel — clear it
        lat: p.lat + offsetLat,
        lng: p.lng + offsetLng,
        _layoutToken: 1,
      };
      if (p.coords && p.coords.length >= 3) {
        copy.coords = p.coords.map(([la, ln]) => [la + offsetLat, ln + offsetLng]);
      }

      const saved = await persistParcel(copy);
      newOnes.push(saved);
    }
    setParcels(prev => [...prev, ...newOnes]);
    setNextNumber(nextNum);
    setSelectedIds(newOnes.map(n => n.parcel_id)); // select the new dupes for easy dragging
  };

  // Bulk apply zone + color to all selected parcels
  const applyZoneToSelection = async (zone) => {
    const updates = selectedParcels.map(p => ({ ...p, zone_name: zone.name, color: zone.color }));
    setParcels(prev => prev.map(p => {
      const u = updates.find(up => up.parcel_id === p.parcel_id);
      return u || p;
    }));
    for (const u of updates) {

      await axios.post(`${API}/api/admin/camp-map/parcels`, u, { headers }).catch(console.error);
    }
  };

  const deleteMarker = async (markerId) => {
    try {
      await axios.delete(`${API}/api/admin/camp-map/markers/${markerId}`, { headers });
      setMarkers(prev => prev.filter(m => m.marker_id !== markerId));
    } catch (err) { console.error(err); }
  };

  const saveMapCenter = async () => {
    try {
      await axios.post(`${API}/api/admin/camp-map/settings`, mapSettings, { headers });
      alert('Pozicija mape spremljena!');
    } catch (err) { console.error(err); }
  };

  // ---- Map events ----
  function MapEvents() {
    const map = useMap();
    useMapEvents({
      moveend: () => {
        const c = map.getCenter();
        setMapSettings(prev => ({ ...prev, lat: c.lat, lng: c.lng, zoom: map.getZoom() }));
      },
    });
    return null;
  }

  if (loading) return <div className="p-8 text-center text-[var(--color-text-muted)]">Ucitavanje kamp mape...</div>;

  const isDrawing = mode === 'draw';
  const isPlacing = mode === 'parcel' || isDrawing || (mode && AMENITY_TYPES.some(a => a.id === mode));

  // Computed mid-points of the actively-drawn polygon edges (for live measurement labels)
  const drawingSegments = isDrawing && drawCoords.length > 0 ? (() => {
    const segs = [];
    for (let i = 0; i < drawCoords.length - 1; i++) {
      const a = drawCoords[i], b = drawCoords[i + 1];
      segs.push({ a, b, mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], len: haversineMeters(a, b) });
    }
    if (hoverPos && drawCoords.length > 0) {
      const last = drawCoords[drawCoords.length - 1];
      segs.push({ a: last, b: hoverPos, mid: [(last[0] + hoverPos[0]) / 2, (last[1] + hoverPos[1]) / 2], len: haversineMeters(last, hoverPos), preview: true });
    }
    return segs;
  })() : [];

  return (
    <div className="space-y-4" data-testid="camp-map-editor" ref={editorRef}>
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Tent size={20} /> Kamp Mapa Editor
          </h3>
          <button onClick={saveMapCenter}
            className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-sm transition-colors"
            style={{ background: 'var(--color-primary)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-primary-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--color-primary)'}
            data-testid="save-map-position">
            <Save size={14} /> Spremi poziciju mape
          </button>
        </div>

        {/* Sticky zone (applies to new parcels) */}
        <div className="rounded-lg p-3 flex flex-wrap items-center gap-3"
          style={{ background: 'var(--color-background-light)', border: '1px solid var(--color-border)' }}
          data-testid="sticky-zone">
          <Layers size={16} style={{ color: 'var(--color-primary)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Aktivna zona:</span>
          <input type="text" value={stickyZone.name}
            onChange={e => setStickyZone(z => ({ ...z, name: e.target.value }))}
            placeholder="npr. Prvi red do mora"
            list="zone-suggestions"
            className="border rounded px-2 py-1 text-sm flex-1 min-w-[180px]"
            data-testid="sticky-zone-name" />
          <datalist id="zone-suggestions">
            {allZoneNames.map(z => <option key={z} value={z} />)}
          </datalist>
          <div className="flex items-center gap-1">
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Boja:</span>
            {PARCEL_COLORS.map(c => (
              <button key={c} onClick={() => setStickyZone(z => ({ ...z, color: c }))}
                className="w-6 h-6 rounded-full transition-transform"
                style={{
                  background: c,
                  border: stickyZone.color === c ? '2px solid var(--color-text-primary)' : '2px solid transparent',
                  transform: stickyZone.color === c ? 'scale(1.15)' : 'scale(1)',
                }}
                data-testid={`sticky-color-${c}`}
                title={c} />
            ))}
          </div>
          <span className="text-xs italic w-full sm:w-auto" style={{ color: 'var(--color-text-muted)' }}>
            Sve nove parcele dobivaju ovu zonu i boju
          </span>
        </div>

        {/* Parcel modes */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => { setMode(mode === 'parcel' ? null : 'parcel'); setDrawCoords([]); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={mode === 'parcel'
              ? { background: 'var(--color-primary)', color: '#fff', boxShadow: '0 0 0 3px var(--color-primary-light)' }
              : { background: 'var(--color-background)', color: 'var(--color-primary)', border: '1px solid var(--color-primary-light)' }}
            data-testid="add-parcel-mode">
            <Square size={16} />
            {mode === 'parcel' ? 'Klikni za pravokutnu parcelu...' : 'Pravokutna parcela'}
          </button>

          <button
            onClick={() => { setMode(mode === 'draw' ? null : 'draw'); setDrawCoords([]); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={mode === 'draw'
              ? { background: 'var(--color-primary)', color: '#fff', boxShadow: '0 0 0 3px var(--color-primary-light)' }
              : { background: 'var(--color-background)', color: 'var(--color-primary)', border: '1px solid var(--color-primary-light)' }}
            data-testid="draw-parcel-mode">
            <PenTool size={16} />
            {mode === 'draw' ? 'Klikaj točke (SHIFT = 90°)' : 'Crtaj parcelu (poligon)'}
          </button>

          {mode === 'parcel' && (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <span>Br:</span>
              <input type="number" value={nextNumber} onChange={e => setNextNumber(parseInt(e.target.value) || 1)}
                className="w-16 border rounded px-2 py-1 text-center text-sm" min={1} />
              <span className="ml-2">Veličina:</span>
              <select value={`${parcelSize.w}x${parcelSize.h}`}
                onChange={e => { const [w, h] = e.target.value.split('x').map(Number); setParcelSize({ w, h }); }}
                className="border rounded px-2 py-1 text-sm">
                <option value="0.5x0.5">Mala</option>
                <option value="1x1">Standardna</option>
                <option value="1.5x1.5">Velika</option>
                <option value="2x2">Vrlo velika</option>
              </select>
            </div>
          )}

          {isDrawing && (
            <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color: 'var(--color-text-secondary)' }}>
              <span>Br: <strong>{nextNumber}</strong></span>
              <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--color-background-light)' }}>
                Točke: {drawCoords.length}
              </span>
              {shiftHeld && (
                <span className="text-xs px-2 py-0.5 rounded text-white" style={{ background: 'var(--color-primary)' }}>
                  SHIFT — 90° snap
                </span>
              )}
              <button onClick={undoLastPoint} disabled={drawCoords.length === 0}
                className="px-2 py-1 text-xs border rounded disabled:opacity-40">Poništi zadnju</button>
              <button onClick={finishDrawing} disabled={drawCoords.length < 3 || saving}
                className="flex items-center gap-1 px-3 py-1 text-xs text-white rounded disabled:opacity-50"
                style={{ background: 'var(--color-primary)' }}
                data-testid="finish-draw-btn">
                <Check size={12} /> Završi (ili 2× klik)
              </button>
              <button onClick={cancelDrawing} className="px-2 py-1 text-xs border rounded">Odustani (ESC)</button>
              <span className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
                Klikni postojeću točku za brisanje
              </span>
            </div>
          )}

          {(mode === 'parcel' || isDrawing) && (
            <button onClick={() => { setMode(null); setDrawCoords([]); }}
              className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          )}
        </div>

        {/* Amenity markers */}
        <div>
          <p className="text-xs font-medium mb-2" style={{ color: 'var(--color-text-muted)' }}>
            Oznake pogodnosti (klikni pa klikni na mapu):
          </p>
          <div className="flex flex-wrap gap-1.5">
            {AMENITY_TYPES.map(amenity => {
              const Icon = amenity.Icon;
              const active = mode === amenity.id;
              return (
                <button key={amenity.id}
                  onClick={() => { setMode(active ? null : amenity.id); setDrawCoords([]); }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                    active ? 'text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={active ? { background: amenity.color } : {}}
                  data-testid={`add-marker-${amenity.id}`}>
                  <Icon size={13} /> {amenity.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Hint bar */}
        <div className="text-xs flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 border-t"
          style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
          <span><Move size={11} className="inline mr-1" /> Povuci broj parcele za premještanje</span>
          <span><Crosshair size={11} className="inline mr-1" /> Vrhovi poligona se mogu povući</span>
          <span><kbd className="px-1 py-0.5 rounded text-[10px] font-mono" style={{ background: 'var(--color-background-light)', border: '1px solid var(--color-border)' }}>Shift</kbd>+klik = multi-select</span>
          <span><kbd className="px-1 py-0.5 rounded text-[10px] font-mono" style={{ background: 'var(--color-background-light)', border: '1px solid var(--color-border)' }}>Ctrl+D</kbd> = dupliciraj</span>
          <span><kbd className="px-1 py-0.5 rounded text-[10px] font-mono" style={{ background: 'var(--color-background-light)', border: '1px solid var(--color-border)' }}>Del</kbd> = obriši</span>
        </div>
      </div>

      {/* Multi-select bulk panel */}
      {selectedIds.length >= 2 && (
        <BulkEditPanel
          count={selectedIds.length}
          stickyZone={stickyZone}
          allZones={allZoneNames}
          onApplyZone={applyZoneToSelection}
          onDuplicate={duplicateSelected}
          onDelete={deleteSelected}
          onClear={clearSelection}
        />
      )}

      {/* Map */}
      <div className="relative rounded-lg overflow-hidden border" style={{ height: '600px' }}>
        <MapContainer center={[mapSettings.lat, mapSettings.lng]} zoom={mapSettings.zoom} maxZoom={22}
          style={{ height: '100%', width: '100%' }} zoomControl={true} doubleClickZoom={!isDrawing}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap"
            maxZoom={22} maxNativeZoom={19} />
          <MapEvents />
          <CursorController active={isPlacing} />
          <MapClickHandler mode={mode} onMapClick={handleMapClick} />
          <MapDoubleClickHandler onDoubleClick={() => isDrawing && drawCoords.length >= 3 && finishDrawing()} />
          <MouseTracker enabled={isDrawing} shiftKey={shiftHeld} lastPoint={drawCoords[drawCoords.length - 1]} onMove={setHoverPos} />

          {/* Saved parcels */}
          {parcels.map(parcel => {
            const sel = isSelected(parcel.parcel_id);
            const isPrimary = primarySelected?.parcel_id === parcel.parcel_id && selectedIds.length === 1;
            const fillColor = parcel.color || '#22C55E';
            const strokeColor = sel ? '#1D4ED8' : fillColor;
            const pathOpts = {
              color: strokeColor,
              weight: sel ? 3 : 2,
              fillOpacity: sel ? 0.5 : 0.35,
              fillColor,
            };
            const onShapeClick = (e) => {
              L.DomEvent.stopPropagation(e);
              toggleSelection(parcel.parcel_id, e.originalEvent?.shiftKey);
            };
            return (
              <React.Fragment key={parcel.parcel_id}>
                {parcel.coords && parcel.coords.length >= 3 ? (
                  <>
                    <Polygon positions={parcel.coords} pathOptions={pathOpts}
                      eventHandlers={{ click: onShapeClick }} />
                    {/* Vertex handles + midpoint inserters - only when single primary selection */}
                    {isPrimary && parcel.coords.map((c, vi) => (
                      <VertexHandle
                        key={`v-${parcel.parcel_id}-${vi}-${parcel.coords.length}-${parcel._layoutToken || 0}`}
                        initialPosition={c}
                        parcelId={parcel.parcel_id}
                        vertexIdx={vi}
                        handlerRef={handlerRef}
                      />
                    ))}
                    {isPrimary && parcel.coords.length >= 3 && parcel.coords.map((c, vi) => {
                      const next = parcel.coords[(vi + 1) % parcel.coords.length];
                      const mid = [(c[0] + next[0]) / 2, (c[1] + next[1]) / 2];
                      return (
                        <Marker key={`m-${parcel.parcel_id}-${vi}-${parcel.coords.length}-${parcel._layoutToken || 0}`}
                          position={mid} icon={midpointIcon}
                          eventHandlers={{ click: () => insertVertex(parcel.parcel_id, vi) }}>
                          <Tooltip direction="top" offset={[0, -6]}>Dodaj točku</Tooltip>
                        </Marker>
                      );
                    })}
                  </>
                ) : (
                  <Rectangle bounds={getRectangleBounds(parcel)} pathOptions={pathOpts}
                    eventHandlers={{ click: onShapeClick }} />
                )}
                {/* Number label — for polygons we use the always-mounted PolygonDragHandle
                    which lets admin grab the whole polygon and move it; for rectangles
                    we keep DraggableParcelMarker (already handles drag via dragend). */}
                {parcel.coords && parcel.coords.length >= 3 ? (
                  <PolygonDragHandle
                    key={`pd-${parcel.parcel_id}-${sel ? '1' : '0'}-${parcel._layoutToken || 0}`}
                    initialPosition={getPolygonCentroid(parcel.coords)}
                    parcelId={parcel.parcel_id}
                    icon={createParcelLabel(parcel.number, parcel.color || '#22C55E', sel)}
                    handlerRef={handlerRef}
                  />
                ) : (
                  <DraggableParcelMarker parcel={parcel} selected={sel}
                    onDragEnd={handleParcelDrag}
                    onClick={(p, ev) => toggleSelection(p.parcel_id, ev?.shiftKey)} />
                )}
              </React.Fragment>
            );
          })}

          {/* Active drawing preview */}
          {isDrawing && drawCoords.length > 0 && (
            <>
              {drawCoords.length >= 3 && (
                <Polygon positions={hoverPos ? [...drawCoords, hoverPos] : drawCoords}
                  pathOptions={{ color: 'var(--color-primary)', dashArray: '6 4', weight: 2, fillOpacity: 0.2, fillColor: stickyZone.color }} />
              )}
              {drawCoords.length < 3 && hoverPos && (
                <Polyline positions={[...drawCoords, hoverPos]} pathOptions={{ color: '#1D4ED8', dashArray: '6 4', weight: 2 }} />
              )}
              {drawCoords.length >= 2 && drawCoords.length < 3 && (
                <Polyline positions={drawCoords} pathOptions={{ color: '#1D4ED8', dashArray: '6 4', weight: 2 }} />
              )}
              {drawCoords.map((c, i) => (
                <CircleMarker key={`dp-${i}`} center={c} radius={7}
                  pathOptions={{ color: '#1D4ED8', fillColor: '#fff', fillOpacity: 1, weight: 2.5 }}
                  eventHandlers={{
                    click: (e) => {
                      L.DomEvent.stopPropagation(e);
                      L.DomEvent.preventDefault(e);
                      // Click on an existing drawn point removes it.
                      setDrawCoords(prev => prev.filter((_, idx) => idx !== i));
                    },
                  }}>
                  <Tooltip direction="top" offset={[0, -8]} permanent={false}>Klikni za brisanje</Tooltip>
                </CircleMarker>
              ))}
              {hoverPos && (
                <CircleMarker center={hoverPos} radius={5}
                  pathOptions={{ color: '#1D4ED8', fillColor: 'var(--color-primary)', fillOpacity: 0.6, weight: 2 }} />
              )}
              {/* Live segment length labels */}
              {drawingSegments.map((s, i) => (
                <Marker key={`seg-${i}`} position={s.mid}
                  icon={L.divIcon({
                    className: 'seg-len',
                    html: `<div style="background:#fff;color:#1F2937;padding:1px 4px;border:1px solid #1D4ED8;border-radius:3px;font-size:10px;font-weight:600;box-shadow:0 1px 2px rgba(0,0,0,0.2);opacity:${s.preview ? 0.75 : 1};">${formatMeters(s.len)}</div>`,
                    iconSize: [40, 16], iconAnchor: [20, 8],
                  })}
                  interactive={false} />
              ))}
            </>
          )}

          {/* Amenity markers */}
          {markers.map(marker => (
            <Marker key={marker.marker_id} position={[marker.lat, marker.lng]} icon={createAmenityIcon(marker.type)}>
              <Popup>
                <div className="text-center">
                  <strong className="text-sm">{marker.label}</strong>
                  <br />
                  <button onClick={() => deleteMarker(marker.marker_id)}
                    className="text-red-600 text-xs underline mt-1">Obriši</button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Status bar */}
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-lg text-xs font-medium shadow z-[1000] flex items-center gap-3"
          style={{ color: 'var(--color-text-primary)' }}>
          <span>Parcele: {parcels.length}</span>
          <span>Oznake: {markers.length}</span>
          {selectedIds.length > 0 && (
            <span className="px-2 py-0.5 rounded text-white" style={{ background: 'var(--color-primary)' }}>
              Odabrano: {selectedIds.length}
            </span>
          )}
          {mode && <span className="font-semibold" style={{ color: 'var(--color-primary)' }}>
            {isDrawing ? `Crtanje · ${drawCoords.length} točaka` : 'Postavljanje aktivno'}
          </span>}
        </div>
      </div>

      {/* Single parcel edit panel */}
      {selectedIds.length === 1 && primarySelected && (
        <SingleParcelEditor
          parcel={primarySelected}
          stickyZone={stickyZone}
          allZones={allZoneNames}
          propertyConfigs={propertyConfigs}
          usedRoomIds={usedRoomIds}
          saving={saving}
          onChange={(updates) => setParcels(prev => prev.map(p => p.parcel_id === primarySelected.parcel_id ? { ...p, ...updates } : p))}
          onSave={() => saveParcel(parcels.find(p => p.parcel_id === primarySelected.parcel_id))}
          onDelete={() => deleteParcel(primarySelected.parcel_id)}
          onClose={() => setSelectedIds([])}
        />
      )}

      {/* Parcels grid (all parcels listing) */}
      {parcels.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Tag size={14} /> Sve parcele ({parcels.length})
          </h4>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-1.5">
            {[...parcels].sort((a, b) => (parseInt(a.number) || 0) - (parseInt(b.number) || 0)).map(p => {
              const sel = isSelected(p.parcel_id);
              return (
                <button key={p.parcel_id}
                  onClick={(e) => toggleSelection(p.parcel_id, e.shiftKey)}
                  className="flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-bold border transition-all"
                  style={sel
                    ? { borderColor: 'var(--color-primary)', background: 'var(--color-primary-light)', color: 'var(--color-primary-dark)' }
                    : { borderColor: 'var(--color-border)' }}
                  data-testid={`parcel-item-${p.number}`}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color || '#22C55E' }} />
                  {p.number}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Bulk edit panel
// ============================================================================
function BulkEditPanel({ count, stickyZone, allZones, onApplyZone, onDuplicate, onDelete, onClear }) {
  const [zoneName, setZoneName] = useState(stickyZone.name);
  const [zoneColor, setZoneColor] = useState(stickyZone.color);
  return (
    <div className="bg-white rounded-lg border-2 p-4 space-y-3"
      style={{ borderColor: 'var(--color-primary)' }}
      data-testid="bulk-edit-panel">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--color-primary)' }}>
          <Layers size={16} /> Odabrano {count} parcela
        </h4>
        <button onClick={onClear} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
      </div>
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Postavi zonu</label>
          <input type="text" value={zoneName} onChange={e => setZoneName(e.target.value)}
            list="bulk-zone-suggest"
            placeholder="npr. Prvi red do mora"
            className="w-full border rounded px-2 py-1.5 text-sm" />
          <datalist id="bulk-zone-suggest">
            {allZones.map(z => <option key={z} value={z} />)}
          </datalist>
        </div>
        <div>
          <label className="text-xs font-medium block" style={{ color: 'var(--color-text-muted)' }}>Boja</label>
          <div className="flex gap-1">
            {PARCEL_COLORS.map(c => (
              <button key={c} onClick={() => setZoneColor(c)}
                className="w-7 h-7 rounded-full"
                style={{
                  background: c,
                  border: zoneColor === c ? '2px solid var(--color-text-primary)' : '2px solid transparent',
                  transform: zoneColor === c ? 'scale(1.1)' : 'scale(1)',
                }} />
            ))}
          </div>
        </div>
        <button onClick={() => onApplyZone({ name: zoneName, color: zoneColor })}
          className="px-3 py-2 text-white rounded text-sm font-medium"
          style={{ background: 'var(--color-primary)' }}
          data-testid="bulk-apply-zone">
          Primijeni na {count}
        </button>
      </div>
      <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <button onClick={onDuplicate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border"
          style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
          data-testid="bulk-duplicate">
          <Copy size={14} /> Dupliciraj (Ctrl+D)
        </button>
        <button onClick={onDelete}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-red-50 text-red-600 hover:bg-red-100"
          data-testid="bulk-delete">
          <Trash2 size={14} /> Obriši sve
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Single parcel edit panel (with grouped room_id dropdown)
// ============================================================================
function SingleParcelEditor({ parcel, allZones, propertyConfigs, usedRoomIds, saving, onChange, onSave, onDelete, onClose }) {
  return (
    <div className="bg-white rounded-lg border p-4 space-y-3" data-testid="parcel-edit-panel">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
          Uredi parcelu #{parcel.number}
          {parcel.coords && <span className="ml-2 text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>· Poligon ({parcel.coords.length} točaka)</span>}
        </h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Broj</label>
          <input type="text" value={parcel.number}
            onChange={e => onChange({ number: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm" data-testid="parcel-number-input" />
        </div>
        <div>
          <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Naziv</label>
          <input type="text" value={parcel.label}
            onChange={e => onChange({ label: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm" data-testid="parcel-label-input" />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Soba (Beds24)</label>
          <select value={parcel.room_id || ''}
            onChange={e => onChange({ room_id: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm bg-white"
            data-testid="parcel-room-id-input">
            <option value="">— Nije povezana —</option>
            {propertyConfigs.length === 0 && (
              <option disabled>Nema konfiguriranih svojstava (Settings → Property Config)</option>
            )}
            {propertyConfigs.map(pc => (
              <optgroup key={pc.property_id} label={`${pc.property_name} (${pc.property_id})`}>
                {(pc.room_ids || []).map(rid => {
                  const used = usedRoomIds.has(String(rid)) && String(rid) !== String(parcel.room_id);
                  return (
                    <option key={rid} value={rid} disabled={used}>
                      {rid}{used ? ' · već u upotrebi' : ''}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Zona</label>
          <input type="text" value={parcel.zone_name || ''}
            onChange={e => onChange({ zone_name: e.target.value })}
            list="single-zone-suggest"
            placeholder="npr. Prvi red do mora"
            className="w-full border rounded px-2 py-1.5 text-sm"
            data-testid="parcel-zone-input" />
          <datalist id="single-zone-suggest">
            {allZones.map(z => <option key={z} value={z} />)}
          </datalist>
        </div>
        {!parcel.coords && (
          <div>
            <label className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Veličina</label>
            <select
              value={`${Math.round((parcel.width || DEFAULT_PARCEL_W) / DEFAULT_PARCEL_W * 10) / 10}`}
              onChange={e => {
                const mult = parseFloat(e.target.value);
                onChange({ width: DEFAULT_PARCEL_W * mult, height: DEFAULT_PARCEL_H * mult });
              }}
              className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="0.5">Mala</option>
              <option value="1">Standardna</option>
              <option value="1.5">Velika</option>
              <option value="2">Vrlo velika</option>
            </select>
          </div>
        )}
      </div>
      <div>
        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--color-text-muted)' }}>Boja zone</label>
        <div className="flex gap-1.5">
          {PARCEL_COLORS.map(c => (
            <button key={c} onClick={() => onChange({ color: c })}
              className="w-7 h-7 rounded-full transition-transform"
              style={{
                background: c,
                border: parcel.color === c ? '2px solid var(--color-text-primary)' : '2px solid transparent',
                transform: parcel.color === c ? 'scale(1.1)' : 'scale(1)',
              }} />
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onSave} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50"
          style={{ background: 'var(--color-primary)' }}
          data-testid="save-parcel-btn">
          <Save size={14} /> {saving ? 'Spremam...' : 'Spremi'}
        </button>
        <button onClick={onDelete}
          className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100"
          data-testid="delete-parcel-btn">
          <Trash2 size={14} /> Obriši
        </button>
      </div>
    </div>
  );
}
